/**
 * @deprecated
 * All functions in this file are deprecated. Please use Logger instead.
 */
import theme from '@splashthat/sui/lib/theme';
import Logger from 'observability/Logger';

const LOG = new Logger('utils-console');
const { console } = window;
const colorCache = {
    normalize: theme.palette.primary.main,
    splash: theme.palette.primary.dark,
    error: theme.palette.danger.main,
    warning: theme.palette.warning.main,
};

/**
 * Generates a random color
 *
 * @return {object} Decomposed color
 */
export function randomColor() {
    return {
        type: 'rgb',
        values: [Math.round(Math.random() * 255), Math.round(Math.random() * 255), Math.round(Math.random() * 255)],
    };
}

/**
 * Converts a color object with type and values to a string.
 *
 * @param {object} color - Decomposed color
 * @param {string} color.type - One of, 'rgb', 'rgba', 'hsl', 'hsla'
 * @param {array} color.values - [n,n,n] or [n,n,n,n]
 * @return {string} CSS color string
 */
export function convertColorToString(color) {
    const { type, values } = color;

    if (type.indexOf('rgb') > -1) {
        // Only convert the first 3 values to int (i.e. not alpha)
        for (let i = 0; i < 3; i++) {
            values[i] = parseInt(values[i], 10);
        }
    }

    let colorString;

    if (type.indexOf('hsl') > -1) {
        colorString = `${color.type}(${values[0]}, ${values[1]}%, ${values[2]}%`;
    } else {
        colorString = `${color.type}(${values[0]}, ${values[1]}, ${values[2]}`;
    }

    if (values.length === 4) {
        colorString += `, ${color.values[3]})`;
    } else {
        colorString += ')';
    }

    return colorString;
}

/**
 * Generates a random color for console logs
 *
 * @param  {String} key Color Key
 * @return {String} #00FF00
 */
function logColor(key) {
    const cacheKey = `${key}`;
    colorCache[cacheKey] || (colorCache[cacheKey] = convertColorToString(randomColor()));
    return colorCache[cacheKey];
}

/**
 * Gets the stack line
 *
 * @param  {Number} [depth] - determines how far up the stack to look for the "real" line number
 * @return {string} stack location
 */
export function getStackLine(depth = 1) {
    try {
        throw new Error();
    } catch (e) {
        const { stack } = e;
        // Missing stack?
        if (!stack) {
            return 'Unknown line';
        }

        const lines = stack.split('\n');
        const line = lines[Math.min(depth + 1, lines.length - 1)];
        return line.replace(/\s*at (?:.*\((.*)\)|(.*))/, '$1$2');
    }
}

/**
 * Set window.SPLASH.printLogs manually in console to view debug logs in non development envs
 *
 * @param {bool}
 */
function shouldPrintLogs() {
    return Boolean(typeof window !== 'undefined' && window.SPLASH && window.SPLASH.printLogs);
}

/**
 * Logs to the console
 *
 * @param  {String} logger - name of the console function to invoke
 * @param  {String} badge - badge type
 * @param  {String} namespace - log namespace, used for color [description]
 * @param  {type} args - log arguments
 */
function baseLog(logger, badge, namespace, stackDepth = 2, ...args) {
    if ((__DEVELOPMENT__ && !__TESTING__) || shouldPrintLogs()) {
        if (console) {
            const color = logColor(namespace);
            if (stackDepth) {
                args.push(getStackLine(stackDepth + 1));
            }
            // Need to use apply because the mix of `||` and `...` makes Babel mangle the context
            (console[logger] || console.log).apply(console, [
                '%c%s%c%s',
                `background-color: ${color}; color: ${theme.palette.grayscale.white};`,
                badge ? ` ${badge} ` : ' ',
                `color: ${color};`,
                namespace ? ` ${namespace}:` : '',
                ...args,
            ]);
        }
    }
    if (!__DEVELOPMENT__) {
        // Outside of the development environment, we should use the Logger to send logs to Datadog.
        // TODO: We will monitor logs in DD to update the code in this repo to use the Logger instead of `utils/console`
        if (stackDepth) {
            args.push(getStackLine(stackDepth + 1));
        }
        const logFn = logger === 'log' ? 'info' : logger;
        LOG[logFn](`[utils-console] ${namespace}:`, { args: { ...args } });
    }
}

/**
 * Prints a log to the console
 * @deprecated
 * Please use Logger instead. (e.g. LOG.info)
 */
export function log(namespace, ...args) {
    baseLog('log', '', namespace, false, ...args);
}

/**
 * Prints a log to the console, including the line number
 * @deprecated
 * Please use Logger instead. (e.g. LOG.info)
 */
export function logWithLine(namespace, ...args) {
    baseLog('log', '', namespace, 2, ...args);
}

/**
 * Prints a warning to the console
 * @deprecated
 * Please use Logger instead. (e.g. LOG.warn)
 */
export function warn(namespace, ...args) {
    baseLog('warn', 'W', namespace, 2, ...args);
}
/**
 * Prints an error to the console
 * @deprecated
 * Please use Logger instead. (e.g. LOG.error)
 */
export function error(namespace, ...args) {
    baseLog('error', 'E', namespace, 2, ...args);
}

/**
 * Marks the start of a console timer
 */
export function time(name) {
    if (__DEVELOPMENT__) {
        if (console && console.time) {
            console.time(name);
        }
    }
}

/**
 * Marks the end of a console timer
 */
export function timeEnd(name) {
    if (__DEVELOPMENT__) {
        if (console && console.timeEnd) {
            console.timeEnd(name);
        }
    }
}

if (__DEVELOPMENT__) {
    window.splashConsole = {
        getStackLine,
        log,
        logWithLine,
        warn,
        error,
        time,
        timeEnd,
    };
}
